import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  Observable
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import axios from 'axios';
import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import rebrand2022 from './common/theme/rebrand2022/rebrand2022';
import old from './common/theme/old';
import { SENTRY_ENVIRONMENT } from './constants';
import Routes from './routes';
import store from './store';
import * as action from './store/actions';
import { extractCSStoHTML, isPrerenderBrowser } from './utils/helper';
import isRebrand from './utils/isRebrand';

if (window.environment === SENTRY_ENVIRONMENT) {
  Sentry.init({
    dsn: 'https://9905ac78346a5e4bd7dfc2b3eba565d7@o443403.ingest.us.sentry.io/4507915114119168',
    integrations: [new Integrations.BrowserTracing()],
    environment: 'production',

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2
  });
}

axios.defaults.withCredentials = true;

store.dispatch(action.authCheckAsync());

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        productBySlug(existingData, { args, toReference }) {
          return (
            existingData ||
            toReference({ __typename: 'Product', slug: args.slug })
          );
        },
        getAllCartProducts: {
          merge(existing = [], incoming) {
            return [...incoming];
          }
        }
      }
    },
    Product: {
      // identifies any product.
      keyFields: ['slug']
    },
    Size: {
      keyFields: ['id', 'is_available_on_pdp']
    },
    Fabric: {
      keyFields: ['id', 'is_available_on_pdp']
    }
  }
});

const request = async operation => { };

let requestLink;

if (isPrerenderBrowser()) {
  requestLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(data => {
      extractCSStoHTML();
      return data;
    });
  });
} else {
  requestLink = new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let handle;
      Promise.resolve(operation)
        .then(oper => request(oper))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    });
  });
}

const client = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        if (graphQLErrors[0].message == 'Internal server error') {
          store.dispatch(action.logoutAsync());
        }
      }
      if (networkError) {
        console.log({ networkError });
      }
    }),
    requestLink,
    new HttpLink({
      uri: '/graphql',
      credentials: 'include'
    })
  ]),
  cache,
  connectToDevTools: window.environment === 'production' ? false : true
});

const theme = getTheme();

render(
  <ApolloProvider client={client}>
    <ThemeProvider theme={theme}>
      <theme.globalStyles />
      <Provider store={store}>
        <Routes />
      </Provider>
    </ThemeProvider>
  </ApolloProvider>,
  document.getElementById('app')
);

function getTheme() {
  return isRebrand() ? rebrand2022 : old;
}
